import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    collection,
    addDoc,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
    ChevronLeft,
    Plus,
    Edit3,
    Trash2,
    Save,
    X,
    AlertCircle,
    CheckCircle,
    Loader2,
    Building2,
    Tag,
    Folder,
    Briefcase,
    DollarSign,
    TrendingUp,
    Award,
    Gift,
    PiggyBank,
    Home,
    Zap,
    Target,
} from "lucide-react";

const XFinanceIncomeCategories = () => {
    const { user } = useUserAuth();
    const navigate = useNavigate();

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [editingCategory, setEditingCategory] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newCategory, setNewCategory] = useState({ nombre: "", icono: "Briefcase", color: "#10B981" });

    const iconOptions = [
        { name: "Briefcase", icon: Briefcase, label: "Trabajo" },
        { name: "DollarSign", icon: DollarSign, label: "Ventas" },
        { name: "TrendingUp", icon: TrendingUp, label: "Inversiones" },
        { name: "Award", icon: Award, label: "Bonos" },
        { name: "Gift", icon: Gift, label: "Regalos" },
        { name: "PiggyBank", icon: PiggyBank, label: "Ahorros" },
        { name: "Home", icon: Home, label: "Rentas" },
        { name: "Zap", icon: Zap, label: "Freelance" },
        { name: "Target", icon: Target, label: "Comisiones" },
        { name: "Tag", icon: Tag, label: "Otros" },
    ];

    const colorOptions = [
        "#10B981", "#3B82F6", "#8B5CF6", "#F59E0B", "#EF4444",
        "#14B8A6", "#F97316", "#6366F1", "#84CC16", "#EC4899"
    ];

    // Verificar autenticación
    useEffect(() => {
        if (!user) {
            navigate("/login");
        }
    }, [user, navigate]);

    // Cargar categorías del usuario
    useEffect(() => {
        if (user) {
            loadCategories();
        }
    }, [user]);

    const loadCategories = async () => {
        if (!user) return;

        setLoadingCategories(true);
        try {
            const categoriesRef = collection(db, "incomes", user.uid, "incomeCategories");
            const q = query(categoriesRef, orderBy("createdAt", "desc"));
            const querySnapshot = await getDocs(q);

            const categoriesData = [];
            querySnapshot.forEach((doc) => {
                categoriesData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });

            setCategories(categoriesData);
        } catch (error) {
            console.error("Error al cargar categorías:", error);
            setMessage({ text: "Error al cargar las categorías", type: "error" });
        } finally {
            setLoadingCategories(false);
        }
    };

    const handleAddCategory = async () => {
        if (!user || !newCategory.nombre.trim()) {
            setMessage({ text: "El nombre de la categoría es requerido", type: "error" });
            return;
        }

        // Verificar si ya existe una categoría con ese nombre
        const existingCategory = categories.find(
            cat => cat.nombre.toLowerCase() === newCategory.nombre.trim().toLowerCase()
        );

        if (existingCategory) {
            setMessage({ text: "Ya existe una categoría con ese nombre", type: "error" });
            return;
        }

        setLoading(true);
        try {
            const categoriesRef = collection(db, "incomes", user.uid, "incomeCategories");
            await addDoc(categoriesRef, {
                nombre: newCategory.nombre.trim(),
                icono: newCategory.icono,
                color: newCategory.color,
                createdAt: new Date(),
                updatedAt: new Date(),
                userId: user.uid,
            });

            setMessage({ text: "Categoría creada correctamente", type: "success" });
            setNewCategory({ nombre: "", icono: "Briefcase", color: "#10B981" });
            setShowAddForm(false);
            loadCategories();
        } catch (error) {
            console.error("Error al crear categoría:", error);
            setMessage({ text: "Error al crear la categoría", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleEditCategory = async (categoryId, updatedData) => {
        if (!user || !updatedData.nombre.trim()) {
            setMessage({ text: "El nombre de la categoría es requerido", type: "error" });
            return;
        }

        // Verificar si ya existe una categoría con ese nombre (excluyendo la actual)
        const existingCategory = categories.find(
            cat => cat.id !== categoryId && cat.nombre.toLowerCase() === updatedData.nombre.trim().toLowerCase()
        );

        if (existingCategory) {
            setMessage({ text: "Ya existe una categoría con ese nombre", type: "error" });
            return;
        }

        setLoading(true);
        try {
            const categoryRef = doc(db, "incomes", user.uid, "incomeCategories", categoryId);
            await updateDoc(categoryRef, {
                nombre: updatedData.nombre.trim(),
                icono: updatedData.icono,
                color: updatedData.color,
                updatedAt: new Date(),
            });

            setMessage({ text: "Categoría actualizada correctamente", type: "success" });
            setEditingCategory(null);
            loadCategories();
        } catch (error) {
            console.error("Error al actualizar categoría:", error);
            setMessage({ text: "Error al actualizar la categoría", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCategory = async (categoryId, categoryName) => {
        if (!user || !window.confirm(`¿Está seguro de eliminar la categoría "${categoryName}"?`)) {
            return;
        }

        setLoading(true);
        try {
            const categoryRef = doc(db, "incomes", user.uid, "incomeCategories", categoryId);
            await deleteDoc(categoryRef);

            setMessage({ text: "Categoría eliminada correctamente", type: "success" });
            loadCategories();
        } catch (error) {
            console.error("Error al eliminar categoría:", error);
            setMessage({ text: "Error al eliminar la categoría", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const createDefaultCategories = async () => {
        if (!user) return;

        const defaultCategories = [
            { nombre: "Salario", icono: "Briefcase", color: "#10B981" },
            { nombre: "Freelance", icono: "Zap", color: "#3B82F6" },
            { nombre: "Inversiones", icono: "TrendingUp", color: "#8B5CF6" },
            { nombre: "Bonos", icono: "Award", color: "#F59E0B" },
            { nombre: "Ventas", icono: "DollarSign", color: "#EF4444" },
            { nombre: "Rentas", icono: "Home", color: "#14B8A6" },
            { nombre: "Comisiones", icono: "Target", color: "#F97316" },
            { nombre: "Otros", icono: "Tag", color: "#6366F1" },
        ];

        setLoading(true);
        try {
            const categoriesRef = collection(db, "incomes", user.uid, "incomeCategories");

            for (const categoryData of defaultCategories) {
                await addDoc(categoriesRef, {
                    ...categoryData,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    userId: user.uid,
                });
            }

            setMessage({ text: "Categorías por defecto creadas correctamente", type: "success" });
            loadCategories();
        } catch (error) {
            console.error("Error al crear categorías por defecto:", error);
            setMessage({ text: "Error al crear las categorías", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const getIconComponent = (iconName) => {
        const iconData = iconOptions.find(opt => opt.name === iconName);
        return iconData ? iconData.icon : Tag;
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <Building2 className="w-16 h-16 text-blue-900 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Acceso no autorizado</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-6">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate(-1)}
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <ChevronLeft className="w-6 h-6 text-slate-600" />
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-slate-900">Categorías de Ingresos</h1>
                                <p className="text-slate-600 font-medium">Gestione las categorías para organizar sus ingresos</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-900 to-blue-800 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-lg">X</span>
                            </div>
                            <span className="text-xl font-bold text-blue-900">XFinance</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Add Category Section */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-8">
                    <div className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                    <Plus className="w-5 h-5 text-green-600" />
                                </div>
                                Nueva Categoría
                            </h2>
                            {categories.length === 0 && (
                                <button
                                    onClick={createDefaultCategories}
                                    disabled={loading}
                                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors disabled:opacity-50"
                                >
                                    Crear Categorías por Defecto
                                </button>
                            )}
                        </div>

                        {showAddForm ? (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            Nombre de la categoría
                                        </label>
                                        <input
                                            type="text"
                                            value={newCategory.nombre}
                                            onChange={(e) => setNewCategory(prev => ({ ...prev, nombre: e.target.value }))}
                                            placeholder="Nombre de la categoría"
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            Color
                                        </label>
                                        <div className="flex gap-2 flex-wrap">
                                            {colorOptions.map(color => (
                                                <button
                                                    key={color}
                                                    onClick={() => setNewCategory(prev => ({ ...prev, color }))}
                                                    className={`w-8 h-8 rounded-lg border-2 transition-all ${newCategory.color === color ? 'border-slate-400 scale-110' : 'border-slate-200'
                                                        }`}
                                                    style={{ backgroundColor: color }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Icono
                                    </label>
                                    <div className="grid grid-cols-5 md:grid-cols-10 gap-3">
                                        {iconOptions.map(({ name, icon: IconComponent, label }) => (
                                            <button
                                                key={name}
                                                onClick={() => setNewCategory(prev => ({ ...prev, icono: name }))}
                                                className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${newCategory.icono === name
                                                        ? 'border-green-500 bg-green-50'
                                                        : 'border-slate-200 hover:border-slate-300'
                                                    }`}
                                                title={label}
                                            >
                                                <IconComponent
                                                    className="w-5 h-5"
                                                    style={{ color: newCategory.color }}
                                                />
                                                <span className="text-xs text-slate-600">{label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <button
                                        onClick={handleAddCategory}
                                        disabled={loading || !newCategory.nombre.trim()}
                                        className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                                    >
                                        <Save className="w-4 h-4" />
                                        Guardar
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowAddForm(false);
                                            setNewCategory({ nombre: "", icono: "Briefcase", color: "#10B981" });
                                        }}
                                        className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowAddForm(true)}
                                className="w-full p-4 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-green-400 hover:text-green-600 transition-colors flex items-center justify-center gap-2 font-medium"
                            >
                                <Plus className="w-5 h-5" />
                                Agregar Nueva Categoría
                            </button>
                        )}
                    </div>
                </div>

                {/* Categories List */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                    <div className="p-6">
                        <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                <Folder className="w-5 h-5 text-green-600" />
                            </div>
                            Categorías Existentes
                            <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm font-medium">
                                {categories.length}
                            </span>
                        </h2>

                        {loadingCategories ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-green-600" />
                                <span className="ml-3 text-slate-600 font-medium">Cargando categorías...</span>
                            </div>
                        ) : categories.length === 0 ? (
                            <div className="text-center py-12">
                                <TrendingUp className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">No hay categorías</h3>
                                <p className="text-slate-600 mb-6">Comience creando su primera categoría</p>
                                <div className="flex gap-3 justify-center">
                                    <button
                                        onClick={() => setShowAddForm(true)}
                                        className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center gap-2"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Nueva Categoría
                                    </button>
                                    <button
                                        onClick={createDefaultCategories}
                                        disabled={loading}
                                        className="border border-slate-300 text-slate-700 px-6 py-3 rounded-lg font-semibold hover:bg-slate-50 transition-colors disabled:opacity-50"
                                    >
                                        Usar Categorías por Defecto
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="grid gap-4">
                                {categories.map((category) => {
                                    const IconComponent = getIconComponent(category.icono);

                                    return (
                                        <div
                                            key={category.id}
                                            className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                                        >
                                            {editingCategory === category.id ? (
                                                <div className="flex items-center gap-3 flex-1">
                                                    <input
                                                        type="text"
                                                        defaultValue={category.nombre}
                                                        className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                handleEditCategory(category.id, {
                                                                    nombre: e.target.value,
                                                                    icono: category.icono,
                                                                    color: category.color
                                                                });
                                                            }
                                                        }}
                                                        onBlur={(e) => handleEditCategory(category.id, {
                                                            nombre: e.target.value,
                                                            icono: category.icono,
                                                            color: category.color
                                                        })}
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() => setEditingCategory(null)}
                                                        className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="flex items-center gap-4">
                                                        <div
                                                            className="w-12 h-12 rounded-lg flex items-center justify-center"
                                                            style={{
                                                                backgroundColor: `${category.color}15`,
                                                                border: `2px solid ${category.color}25`
                                                            }}
                                                        >
                                                            <IconComponent
                                                                className="w-6 h-6"
                                                                style={{ color: category.color }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-semibold text-slate-900">{category.nombre}</h3>
                                                            <p className="text-sm text-slate-500">
                                                                Creada el {new Date(category.createdAt?.toDate()).toLocaleDateString("es-MX")}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => setEditingCategory(category.id)}
                                                            className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                                            title="Editar categoría"
                                                        >
                                                            <Edit3 className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteCategory(category.id, category.nombre)}
                                                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                                            title="Eliminar categoría"
                                                            disabled={loading}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Info Card */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 mt-8">
                    <h3 className="text-lg font-semibold text-green-900 mb-3 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Información sobre Categorías de Ingresos
                    </h3>
                    <ul className="space-y-2 text-green-800">
                        <li className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-green-600 rounded-full"></div>
                            Las categorías le ayudan a organizar y clasificar sus fuentes de ingresos
                        </li>
                        <li className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-green-600 rounded-full"></div>
                            Puede personalizar el color e icono de cada categoría
                        </li>
                        <li className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-green-600 rounded-full"></div>
                            Las categorías se utilizan para filtrar y generar reportes detallados
                        </li>
                    </ul>
                </div>
            </div>

            {/* Toast Message */}
            {message.text && (
                <div
                    className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg transition-all duration-300 z-50 max-w-md ${message.type === "error"
                            ? "bg-red-50 border border-red-200 text-red-800"
                            : "bg-green-50 border border-green-200 text-green-800"
                        }`}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {message.type === "error" ? (
                                <AlertCircle className="w-5 h-5 text-red-600" />
                            ) : (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            )}
                            <span className="font-semibold">{message.text}</span>
                        </div>
                        <button
                            onClick={() => setMessage({ text: "", type: "" })}
                            className="ml-4 p-1 hover:bg-white/50 rounded-full transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default XFinanceIncomeCategories;